import React, { useEffect, useState } from 'react';
import './CatalogWarmup.css';
import { getCachedResource, loadResource } from '../storeData';
import moneda from '../moneda';
import { catalogImageUrl } from '../imageUrl';

const WARMUP_DONE_KEY = 'showphai-catalog-warmup';
const MIN_VISIBLE_MS = 3200;
const MAX_VISIBLE_MS = 6500;
const IMAGE_TIMEOUT_MS = 2600;

function shouldShowWarmup() {
    try {
        if (sessionStorage.getItem(WARMUP_DONE_KEY) === '1') {
            return false;
        }
    } catch (error) {
        // Continue without session storage when the browser blocks it.
    }

    return !(getCachedResource('banners') && getCachedResource('productos'));
}

function getProductImage(product) {
    return catalogImageUrl(product?.imagen1 || product?.imagen2 || product?.imagen3 || product?.imagen4 || '');
}

function preloadImage(src) {
    return new Promise((resolve) => {
        if (!src) {
            resolve();
            return;
        }

        const image = new Image();
        const timer = window.setTimeout(resolve, IMAGE_TIMEOUT_MS);

        image.onload = () => {
            window.clearTimeout(timer);
            resolve();
        };

        image.onerror = () => {
            window.clearTimeout(timer);
            resolve();
        };

        image.src = src;
    });
}

function delay(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function markWarmupDone() {
    try {
        sessionStorage.setItem(WARMUP_DONE_KEY, '1');
    } catch (error) {
        // Ignore storage errors.
    }
}

export default function CatalogWarmup() {
    const [visible, setVisible] = useState(shouldShowWarmup);
    const [progress, setProgress] = useState(8);

    useEffect(() => {
        if (!visible) return undefined;

        let active = true;
        document.body.classList.add('catalogWarmupLocked');

        const progressTimer = window.setInterval(() => {
            setProgress((current) => {
                if (current >= 92) return current;
                if (current < 45) return current + 5;
                if (current < 76) return current + 3;
                return current + 1;
            });
        }, 150);

        const warmCatalog = Promise.all([
            loadResource('banners'),
            loadResource('productos'),
            loadResource('secciones'),
            loadResource('categorias'),
            loadResource('subcategorias'),
        ])
            .then(([banners, products]) => {
                const bannerImages = (banners || [])
                    .map((banner) => banner.imagen)
                    .filter(Boolean)
                    .slice(0, 3);

                const bestSellers = (products || [])
                    .filter((product) => String(product.masVendido || '').toLowerCase() === 'si')
                    .map(getProductImage)
                    .filter(Boolean)
                    .slice(0, 8);

                const firstProducts = (products || [])
                    .map(getProductImage)
                    .filter(Boolean)
                    .slice(0, 10);

                const images = [...new Set([...bannerImages, ...bestSellers, ...firstProducts])];
                return Promise.all(images.map(preloadImage));
            })
            .catch(() => null);

        Promise.all([
            delay(MIN_VISIBLE_MS),
            Promise.race([warmCatalog, delay(MAX_VISIBLE_MS)]),
        ]).then(() => {
            if (!active) return;

            setProgress(100);
            window.setTimeout(() => {
                if (!active) return;
                markWarmupDone();
                setVisible(false);
            }, 320);
        });

        return () => {
            active = false;
            window.clearInterval(progressTimer);
            document.body.classList.remove('catalogWarmupLocked');
        };
    }, [visible]);

    useEffect(() => {
        if (!visible) {
            document.body.classList.remove('catalogWarmupLocked');
        }
    }, [visible]);

    if (!visible) {
        return null;
    }

    return (
        <section className='catalogWarmup' aria-live='polite'>
            <div className='catalogWarmupCard'>
                <div className='catalogWarmupMark'>
                    <span>SHOPHAI</span>
                    <small>Pagas cuando recibes</small>
                </div>

                <div className='catalogWarmupHero'>
                    <p>Compra minima desde</p>
                    <strong>{moneda} 70.000</strong>
                </div>

                <div className='catalogWarmupSteps'>
                    <div>
                        <span>1</span>
                        <p>Elige tus productos</p>
                    </div>
                    <div>
                        <span>2</span>
                        <p>Selecciona ciudad y entrega</p>
                    </div>
                    <div>
                        <span>3</span>
                        <p>Registra cedula y WhatsApp</p>
                    </div>
                    <div>
                        <span>4</span>
                        <p>Recibe y paga al entregar</p>
                    </div>
                </div>

                <div className='catalogWarmupProgress'>
                    <div className='catalogWarmupProgressInfo'>
                        <span>Preparando tu catalogo</span>
                        <strong>{Math.round(progress)}%</strong>
                    </div>
                    <div className='catalogWarmupTrack'>
                        <div style={{ width: `${progress}%` }} />
                    </div>
                </div>
            </div>
        </section>
    );
}
